import {
  AuthCredential,
  OAuthCredential,
  OAuthProvider,
  SAMLAuthProvider,
} from 'firebase/auth';

export type SsoRedirectState = {
  organizationSlug: string;
  returnUrl: string;
  credentialToLink?: string;
  initiatedAt: number;
};

const SSO_STATE_KEY = 'xf-sso-redirect-state';
const STATE_EXPIRY_MS = 5 * 60 * 1000;

export const useSsoRedirect = () => {
  const clearState = () => {
    if (globalThis.window === undefined) return;
    sessionStorage.removeItem(SSO_STATE_KEY);
  };

  const saveState = (state: Omit<SsoRedirectState, 'initiatedAt'>) => {
    if (globalThis.window === undefined) return;

    const fullState: SsoRedirectState = {
      ...state,
      initiatedAt: Date.now(),
    };
    sessionStorage.setItem(SSO_STATE_KEY, JSON.stringify(fullState));
  };

  const getState = (): SsoRedirectState | null => {
    if (globalThis.window === undefined) return null;

    const stored = sessionStorage.getItem(SSO_STATE_KEY);
    if (!stored) return null;

    try {
      const state: SsoRedirectState = JSON.parse(stored);

      if (Date.now() - state.initiatedAt > STATE_EXPIRY_MS) {
        clearState();
        return null;
      }

      return state;
    } catch {
      clearState();
      return null;
    }
  };

  const serializeCredential = (credential: AuthCredential): string => {
    return JSON.stringify(credential.toJSON());
  };

  const deserializeCredential = (
    json: string,
    providerId: string,
  ): OAuthCredential | null => {
    try {
      const parsed = JSON.parse(json);
      const credential = providerId.startsWith('saml.')
        ? SAMLAuthProvider.credentialFromJSON(parsed)
        : OAuthProvider.credentialFromJSON(parsed);
      return credential as OAuthCredential;
    } catch {
      return null;
    }
  };

  return {
    saveState,
    getState,
    clearState,
    serializeCredential,
    deserializeCredential,
  };
};
